import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";

import Loading from "@/components/Loading";
import Page from "@/components/Page";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import ViewField from "@/components/Input/ViewField";
import VLFModal from "@/components/Modal";
import AreaPermissionsEditor from "@/components/AreaPermissionsEditor";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import RestoreIcon from "@mui/icons-material/Restore";

import {UserRoleAPI} from "@/api/user_role";
import {UserAPI} from "@/api/user";

import {ERROR_OPERATION, SUCCESSFUL_OPERATION} from "@/utils/constants";

const SYSTEM_ROLES = ["Admin", "SuperUser", "Staff", "Player"];

function UserRolePage(props) {
	const {handleNotification} = props;
	const {id} = useParams();
	const navigate = useNavigate();

	const [loading, setLoading] = useState(false);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [userRoleData, setUserRoleData] = useState({});
	const [rolePerms, setRolePerms] = useState([]);

	const getUserRole = async () => {
		setLoading(true);
		try {
			const response = await UserRoleAPI.getById(id);
			setUserRoleData(response);
			if (response?.id) {
				try {
					const perms = await UserAPI.getRolePermissions(response.id);
					setRolePerms(perms || []);
				} catch {
					setRolePerms([]);
				}
			}
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getUserRole();
	}, [id]);

	const deleteUserRole = async () => {
		setLoading(true);
		try {
			await UserRoleAPI.delete(userRoleData?.name);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/settings/user-roles");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const saveRolePermissions = async () => {
		if (!userRoleData?.id) return;
		setLoading(true);
		try {
			await UserAPI.setRolePermissions(userRoleData.id, rolePerms);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const proposeDefaults = async () => {
		setLoading(true);
		try {
			setRolePerms((await UserAPI.getRoleDefaults(userRoleData.name)) || []);
			handleNotification(
				"Profilo proposto. Premi Salva permessi per applicarlo al ruolo.",
				"info",
			);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const mainConfiguration = [
		{
			value: userRoleData?.name,
			label: "Nome",
			size: "col-12 col-sm-6 col-lg-4",
		},
	];

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate("/settings/user-roles"),
			color: "info",
		},
		...(["Staff", "Player"].includes(userRoleData?.name)
			? [
					{
						icon: <RestoreIcon />,
						title: "Proponi profilo predefinito",
						onClick: proposeDefaults,
						variant: "outlined",
					},
				]
			: []),
		{
			icon: <SaveIcon />,
			title: "Salva permessi",
			onClick: saveRolePermissions,
		},
		// I quattro ruoli di sistema servono all'app: non si eliminano
		...(userRoleData?.name && !SYSTEM_ROLES.includes(userRoleData.name)
			? [
					{
						icon: <DeleteIcon />,
						title: "Elimina ruolo",
						variant: "outlined",
						onClick: () => setOpenHandleConfirm(true),
						color: "error",
					},
				]
			: []),
	];

	const breadcrumbsElements = [
		{label: "Ruoli e permessi", path: "/settings/user-roles"},
		{label: "/"},
	];

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={userRoleData ? userRoleData.name : ""}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni principali"
							content={
								<div className="row">
									<div className="col-sm-12">
										<ViewField configuration={mainConfiguration} />
									</div>
								</div>
							}
							openable={false}
						/>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Permessi per area"
							content={
								<>
									<div className="text-secondary small mb-2">
										Valgono per tutti gli utenti con questo ruolo; per un
										singolo utente si cambiano dalla sua scheda.
									</div>
									<AreaPermissionsEditor
										mode="role"
										value={rolePerms}
										onChange={setRolePerms}
									/>
								</>
							}
							openable={false}
						/>
					</div>
				</div>
			</div>
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">Eliminare il ruolo {userRoleData?.name}?</div>
				}
				confirm={() => deleteUserRole()}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Elimina"
			/>
		</Page>
	);
}

export default UserRolePage;
